import React, { useState, useEffect, useContext, useMemo } from 'react';
import { apiRequest } from '../api';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../apiConfig';
import './PlayedGameHistory.css';
import arrowIcon from '../assets/icons/arrow-icon.png';
import { commonIcons } from './assetImports';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PlayedGameHistory = ({ isTelegramMiniApp }) => {
  const { user } = useContext(AuthContext);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t, ready } = useTranslation('gameHistory');

  const getCurrencyIcon = (currency) => {
    switch (currency) {
      case 'stars':
        return (
          <img
            src={commonIcons.starIcon}
            alt="Stars"
            className="playedGamesHistory-currency-icon playedGamesHistory-currency-icon-stars"
          />
        );
      case 'token':
        return (
          <img
            src={commonIcons.tokenYellow}
            alt="Token"
            className="playedGamesHistory-currency-icon playedGamesHistory-currency-icon-token"
          />
        );
      default:
        return (
          <img
            src={commonIcons.usdtIcon}
            alt="USDT"
            className="playedGamesHistory-currency-icon playedGamesHistory-currency-icon-usdt"
          />
        );
    }
  };

  useEffect(() => {
    const fetchGames = async () => {
      try {
        setLoading(true);
        const response = await apiRequest(
          `${API_BASE_URL}/api/played-games-history/${user._id}`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        if (response.ok) {
          const data = await response.json();
          setGames(data.games);
        } else {
          setError('error');
        }
      } catch (err) {
        setError('error');
      } finally {
        setLoading(false);
      }
    };

    if (user && user._id) {
      fetchGames();
    }
  }, [user]);

  const filteredGames = useMemo(() => {
    return games
      .filter((game) =>
        isTelegramMiniApp
          ? game.currency === 'stars' || game.currency === 'token'
          : game.currency === 'usdt' || game.currency === 'token'
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [games, isTelegramMiniApp]);

  const groupedGames = useMemo(() => {
    if (!ready) return {};

    const months = t('months', { returnObjects: true });

    return filteredGames.reduce((acc, game) => {
      const dateObj = new Date(game.date);
      const monthIndex = dateObj.getMonth();
      const monthName = months[monthIndex] || '';
      const dateKey = `${dateObj.getDate()} ${monthName}`;

      const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(
        dateObj.getMonth() + 1
      ).padStart(2, '0')}.${dateObj.getFullYear()}, ${String(dateObj.getHours()).padStart(
        2,
        '0'
      )}:${String(dateObj.getMinutes()).padStart(2, '0')}:${String(
        dateObj.getSeconds()
      ).padStart(2, '0')}`;

      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push({ ...game, formattedDate });
      return acc;
    }, {});
  }, [filteredGames, ready, t]);

  if (loading || !ready) {
    return (
      <div className="playedGamesHistory-container">
        <div className="playedGamesHistory-loading-message">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="playedGamesHistory-container">
        <div className="playedGamesHistory-error-message">{t(error)}</div>
      </div>
    );
  }

  return (
    <div className="playedGamesHistory-container">
      <div className="playedGamesHistory-header">
        {!isTelegramMiniApp && (
          <button
            onClick={() => navigate(-1)}
            className="playedGamesHistory-back-button"
          >
            <img
              src={arrowIcon}
              alt="back"
              className="playedGamesHistory-back-icon"
            />
          </button>
        )}
        <h2 className="playedGamesHistory-page-title">{t('title')}</h2>
      </div>

      <div className="playedGamesHistory-list">
        {Object.keys(groupedGames).length > 0 ? (
          Object.entries(groupedGames).map(([date, games]) => (
            <div key={date} className="playedGamesHistory-group">
              <h3 className="playedGamesHistory-group-date">{date}</h3>
              {games.map((game) => (
                <div key={game._id} className="playedGamesHistory-item">
                  <div className="playedGamesHistory-info">
                    <div className="playedGamesHistory-name">
                      {game.gameName && typeof game.gameName === 'string'
                        ? t(`games.${game.gameName}`, {
                            defaultValue: game.gameName,
                          })
                        : ''}
                    </div>
                    <div className="playedGamesHistory-date">
                      {game.formattedDate}
                    </div>
                  </div>
                  <div className="playedGamesHistory-result">
                    <span
                      className={`playedGamesHistory-result-value ${game.result.toLowerCase()}`}
                    >
                      {game.result === 'win' ? '+' : '-'}
                      {game.currency === 'usdt'
                        ? game.bet.toFixed(2)
                        : game.bet}
                    </span>
                    {getCurrencyIcon(game.currency || 'usdt')}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="playedGamesHistory-empty-message">{t('empty')}</div>
        )}
      </div>
    </div>
  );
};

export default PlayedGameHistory;
